import { useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Flex } from '@chakra-ui/react';
import { Button, Tabs } from '@kula-ai/components';
// The file, not the `components/ats` barrel. That barrel also carries
// ApplicationActions, which now reaches this wizard through the ad-hoc drawer —
// importing it back here closes a cycle `pnpm circular:check` rejects.
import { CenterSpinner } from '@/components/ats/loadders/CenterSpinner';
import { useShowToast } from '@/hooks';
import { atsJobCreateApiActions } from '@/store/atsJobCreate';
import {
  useCreateEsignActivityMutation,
  useGetActivityQuery,
  useUpdateEsignActivityMutation,
} from '@/store/query/interview-plan';
import {
  EsignEmailForm,
  type EsignEmailFormRef,
} from '@/components/contacts/candidate-job-drawer/tabs/current-job/details/OfferV1/components/offer-email/EsignEmailForm';
import {
  ESIGN_ACTIVITY_COPY,
  ESIGN_ACTIVITY_STEPS,
  INTEGRATION_TYPES,
} from '@/constants/esignature';
import type {
  DefaultEsignEnvelope,
  EsignActivityConfig,
  EsignActivityPayload,
} from '@/types/esignature';
import {
  useInterviewStageManager,
  type InterviewStageActivityAction,
} from '@/context-providers/interviewStageManager';
import {
  getActivityLoadTarget,
  getCloneSourceParams,
  withCloneName,
} from '@/components/shared/interview-plan/clone/cloneActivity';
import { EsignDocumentsStep } from './EsignDocumentsStep';
import { EsignDocumentPreview } from './EsignDocumentPreview';
import { EsignDetailsStep } from './EsignDetailsStep';
import { useEsignDocuments } from './useEsignDocuments';
import { usePreviewEsignDocuments } from './usePreviewEsignDocuments';
import { useEsignSupportedTokens } from './useEsignSupportedTokens';
import { buildEsignSendOverrides } from '@/store/query/esign-documents/esignSendOverrides';
import { esignActivityErrorCopy } from './esignActivityErrors';

export interface EsignActivityProps {
  onClose?: () => void;
  /**
   * Which activity to open, and the job it belongs to.
   *
   * The interview plan supplies these through its stage-manager context. The
   * candidate row has no such context — it already holds the stage activity —
   * so it passes them directly instead. The context declares a default value,
   * so reading it outside its provider is safe and simply yields nothing to
   * fall back to.
   */
  activityAction?: InterviewStageActivityAction;
  jobId?: string | number;
  /** Called after a successful save; the plan uses it to refetch itself. */
  onSaved?: () => void;
  /**
   * Candidate this envelope would be built for.
   *
   * Only the ad-hoc surface has one. Preview creates a real DocuSign draft, so
   * `POST /esign_documents/preview` requires an application id — from the
   * interview plan there is no candidate yet, and the endpoint has no parameter
   * set that works without one. The button is therefore rendered only when an
   * application is in context; a plan-side preview is blocked on the backend
   * accepting a preview with no application.
   */
  applicationId?: string | number;
  /**
   * A one-off send on a stage with no configured e-sign activity.
   *
   * There is nothing to seed from and nothing to save: the wizard opens empty
   * and the whole payload travels with the request, landing under
   * `stage.adhoc_esign_documents[]` rather than becoming a planned activity.
   * Tailoring a *configured* activity for one candidate is not this — that is
   * the same wizard with `activityAction`, previewed with overrides.
   */
  isAdhoc?: boolean;
  /**
   * What a previous send actually contained, captured off the row before its
   * envelope was voided.
   *
   * Takes precedence over the stage's configuration field by field. Without it
   * an Edit reseeds from the stage template and silently discards whatever was
   * tailored for this candidate last time.
   */
  seed?: EsignActivityConfig;
}

/**
 * "Docusign e-sign document" as an interview-plan stage activity.
 *
 * Two steps: upload and validate the .docx documents, then set the signers and
 * the per-recipient emails. Step 2 reuses the offer flow's composer verbatim —
 * the email model (per-recipient toggle, company signer, CC recipients) is
 * identical — seeded from this activity rather than from the account default.
 */
export const EsignActivity = ({
  onClose = () => {},
  activityAction: activityActionProp,
  jobId: jobIdProp,
  onSaved,
  applicationId,
  isAdhoc = false,
  seed,
}: EsignActivityProps) => {
  const toast = useShowToast();
  const dispatch = useDispatch();
  const {
    activityAction: contextActivityAction,
    jobId: rawJobId,
    refresh: contextRefresh,
  } = useInterviewStageManager();
  const activityAction = activityActionProp ?? contextActivityAction;
  // `jobId` is typed `unknown` on the stage-manager context. Narrow once here
  // rather than casting at each call site that passes it to a typed hook.
  const jobId = (jobIdProp ?? rawJobId) as string | number | undefined;
  const refresh = onSaved ?? contextRefresh;
  const { stage, activity } = activityAction ?? {};
  const isCreateFlow = activityAction?.type === 'new';

  const loadTarget = getActivityLoadTarget(activityAction);
  const { data: loadedActivity, isLoading: isLoadingActivity } =
    useGetActivityQuery(
      { activityId: loadTarget.activityId, stageId: loadTarget.stageId, jobId },
      { skip: !loadTarget.activityId }
    );
  // A clone keeps the source's documents (sent back as ids; the backend
  // copies them) under a "Copy of" label. `data ?? {}` would be a fresh
  // literal every render, which would make the composer-seed memo below
  // recompute and re-initialise the editor, so withCloneName's fallback
  // reuses the same stable-identity pattern.
  //
  // A captured `seed` overlays that rather than replacing it: the previous send
  // is authoritative for what it carried, but anything it did not carry — a
  // label, a signer requirement it never touched — should still come from the
  // activity. It layers over the clone name for the same reason.
  const config = useMemo(
    () =>
      ({
        ...(withCloneName(loadedActivity, activityAction) ?? {}),
        ...(seed ?? {}),
      }) as EsignActivityConfig,
    [loadedActivity, activityAction, seed]
  );

  const [step, setStep] = useState<string>(ESIGN_ACTIVITY_STEPS.DOCUMENTS);
  const [previewKey, setPreviewKey] = useState<string | undefined>();
  const esignFormRef = useRef<EsignEmailFormRef>(null);

  const [createEsignActivity, { isLoading: isCreating }] =
    useCreateEsignActivityMutation();
  const [updateEsignActivity, { isLoading: isUpdating }] =
    useUpdateEsignActivityMutation();
  const { runPreview, runAdhocPreview, isPreviewing } =
    usePreviewEsignDocuments();
  // Which anchors the document set must carry now comes from the server's
  // catalogue rather than a constant here.
  const { requiredTokens } = useEsignSupportedTokens();

  const {
    documents,
    isValidating,
    canContinue,
    addFiles,
    removeDocument,
    reorderDocuments,
  } = useEsignDocuments({
    jobId,
    stageId: stage?.id,
    savedDocuments: config.esign_documents,
    requiredTokens,
  });

  /**
   * The composer speaks the offer envelope's field names, so the activity's
   * `employer_signer_required` is mapped back to `company_signer_required` on
   * the way in. The reverse mapping lives in getEsignFormData.
   */
  const composerInitialValues = useMemo<DefaultEsignEnvelope>(
    () => ({
      company_signer_required: Boolean(config.employer_signer_required),
      // The composer seeds its signer dropdown from `company_signer.id`, but
      // the activity is saved with a flat `company_signer_id` — so a config
      // that echoes back only the id left the field empty on reopen, with the
      // signer silently unset. Read whichever the payload carries.
      company_signer:
        config.company_signer ??
        (config.company_signer_id
          ? { id: config.company_signer_id }
          : undefined),
      signing_order: config.signing_order ?? 'company_signer_first',
      email_subject: config.email_subject ?? '',
      email_body: config.email_body ?? '',
      custom_message_per_recipient: Boolean(
        config.custom_message_per_recipient
      ),
      company_email_subject: config.company_email_subject ?? '',
      company_email_body: config.company_email_body ?? '',
      cc_email_subject: config.cc_email_subject ?? '',
      cc_email_body: config.cc_email_body ?? '',
      cc_user_ids: config.cc_user_ids ?? [],
    }),
    [config]
  );

  const buildPayload = async (): Promise<EsignActivityPayload | null> => {
    const result = await esignFormRef.current?.getModel();
    // getModel toasts its own validation failures (missing subject, empty
    // variables), so a second message here would double up.
    if (!result?.hasValidData || !result.rawValues) return null;

    return {
      label: config.label,
      documents,
      ...result.rawValues,
    };
  };

  const persist = async (payload: EsignActivityPayload) => {
    const mutation = isCreateFlow ? createEsignActivity : updateEsignActivity;
    return mutation({
      payload: {
        jobId,
        stageId: stage?.id,
        activityId: config.id ?? activity?.id,
        params: isCreateFlow
          ? { ...payload, ...getCloneSourceParams(activityAction) }
          : payload,
      },
    });
  };

  const handleSave = async () => {
    const payload = await buildPayload();
    if (!payload) return;

    const response = await persist(payload);
    if ('error' in response) {
      toast({ title: esignActivityErrorCopy(response.error), status: 'error' });
      return;
    }

    dispatch(
      atsJobCreateApiActions('getInterviewPlan', {
        pathParams: { jobId },
        isBackground: true,
      })
    );
    toast({ title: ESIGN_ACTIVITY_COPY.SAVE_SUCCESS });
    onClose();
    refresh?.();
  };

  /**
   * "Preview on Docusign" from a candidate's drawer.
   *
   * Previews the stage activity for this one candidate, carrying only what the
   * recruiter actually changed. Nothing is persisted: saving would go through
   * the stage-action config endpoint, which is the stage's *template* — it would
   * rewrite the message and replace the document set for every other candidate
   * on that stage who has not been sent yet, and for every future one, without
   * telling anyone.
   *
   * The overrides ride on `/preview` rather than `/preview_adhoc` because this
   * IS a planned activity. Ad-hoc is for a stage with no e-sign configured at
   * all; using it here would detach the envelope from its stage action and land
   * it under `adhoc_esign_documents[]` — a different row, outside the activity's
   * status machine.
   */
  const handlePreview = async () => {
    if (!applicationId) return;

    // A one-off send has no stage action to hang off, so the whole payload
    // travels and the stage id is what groups the envelope onto the row.
    if (isAdhoc) {
      if (!stage?.id) return;
      const payload = await buildPayload();
      if (!payload) return;

      await runAdhocPreview({
        atsApplicationId: applicationId,
        atsJobStageId: stage.id,
        params: payload,
      });
      return;
    }

    const activityId = config.id ?? activity?.id;
    if (!activityId) return;
    const payload = await buildPayload();
    if (!payload) return;

    await runPreview({
      atsJobStageActionId: activityId,
      atsApplicationId: applicationId,
      overrides: buildEsignSendOverrides(payload, config),
    });
  };

  const isDocumentsStep = step === ESIGN_ACTIVITY_STEPS.DOCUMENTS;
  const isSaving = isCreating || isUpdating;

  if (isLoadingActivity) {
    return <CenterSpinner />;
  }

  return (
    <>
      <Flex
        flexDir="column"
        flex="1"
        minH={0}
        data-test-id="esign-activity-body"
      >
        {/* Figma's header (7:4983) is ~97px: 16px top, a title row, a 16px gap,
            a 21px stepper and 20px of tail. kula's stepper is 32px rather than
            21px, so the band's own padding is trimmed to land on the same total
            height instead of stacking spec padding around a taller component. */}
        <Box
          px="24px"
          pt="8px"
          pb="16px"
          borderBottom="1px solid"
          borderColor="border-color"
        >
          <Tabs.Root
            variant="stepper"
            value={step}
            onValueChange={setStep}
            // Same configuration the Schedule Interview wizard uses
            // (ScheduleContent.tsx): cap the width so the steps stay a
            // left-aligned group instead of stretching edge to edge, and kill
            // kula's tab-list rule, which would otherwise sit on top of the
            // header's own full-width one as a short second line. Padding is
            // zeroed because this drawer's header band already supplies the
            // 24px gutter that aligns the stepper with the title.
            tabListWrapperClassName="shrink-0 max-w-[600px] !border-b-0 p-0"
            // No panels are used — the step content renders below — but kula
            // still lays them out, and their default top margin added 17px of
            // dead space under the stepper.
            panelClassName="!mt-0"
            // Trigger padding adds 8px on top of the 28px marker for no gain.
            triggerClassName="!py-0"
            // Step 2 needs a validated document set before it can submit, so
            // it stays locked until step 1 passes rather than failing on Save.
            items={[
              {
                value: ESIGN_ACTIVITY_STEPS.DOCUMENTS,
                label: ESIGN_ACTIVITY_COPY.STEP_ONE_LABEL,
                // Completed means "left behind", not "valid". Figma 2:20071
                // still shows the numbered circle with a verified document on
                // screen; the tick only appears from step 2 (64:11459).
                completed: !isDocumentsStep,
              },
              {
                value: ESIGN_ACTIVITY_STEPS.DETAILS,
                label: ESIGN_ACTIVITY_COPY.STEP_TWO_LABEL,
                disabled: !canContinue,
              },
            ]}
          />
        </Box>

        {isDocumentsStep ? (
          <EsignDocumentsStep
            documents={documents}
            isValidating={isValidating}
            onFilesAdded={addFiles}
            onRemove={removeDocument}
            onReorder={reorderDocuments}
            onPreview={(document) => setPreviewKey(document.key)}
            // Download is a plain fetch of the stored file; an upload that has
            // not been saved yet has no URL, and its bytes are still local.
            onDownload={(document) =>
              document.url && window.open(document.url, '_blank', 'noreferrer')
            }
          />
        ) : (
          <EsignDetailsStep>
            <EsignEmailForm
              ref={esignFormRef}
              isAdmin
              integrationType={INTEGRATION_TYPES.DOCUSIGN}
              jobId={jobId}
              initialValues={composerInitialValues}
            />
          </EsignDetailsStep>
        )}
      </Flex>

      {/* Footer padding is 16px 12px 16px 0 with a 6px button gap (Figma
          7:4983), so the primary sits 12px off the drawer's right edge. */}
      <Flex
        p="16px 12px 16px 0"
        borderTop="1px solid"
        borderColor="border-color"
        bg="white"
        flexShrink={0}
        data-test-id="esign-activity-footer"
      >
        <Flex ml="auto" gap="6px">
          <Button
            variant="secondary"
            onClick={
              isDocumentsStep
                ? onClose
                : () => setStep(ESIGN_ACTIVITY_STEPS.DOCUMENTS)
            }
            data-test-id="secondary-cta-button"
          >
            {isDocumentsStep
              ? ESIGN_ACTIVITY_COPY.CANCEL
              : ESIGN_ACTIVITY_COPY.BACK}
          </Button>
          {isDocumentsStep ? (
            <Button
              variant="primary"
              disabled={!canContinue}
              onClick={() => setStep(ESIGN_ACTIVITY_STEPS.DETAILS)}
              data-test-id="primary-cta-button"
            >
              {ESIGN_ACTIVITY_COPY.NEXT}
            </Button>
          ) : (
            /* With a candidate in hand this is the send flow, and its step 2
               ends at "Preview on Docusign" (Figma 25:12313, frame 3). Save is
               deliberately absent there: this wizard is seeded from the stage
               activity, which is shared by every candidate on the stage, so
               saving from one candidate's drawer would rewrite the
               configuration for all of them. From the interview plan — no
               application — Save is the whole point and preview is impossible,
               since both preview endpoints require an application id. */
            <Button
              variant="primary"
              onClick={applicationId ? handlePreview : handleSave}
              isLoading={applicationId ? isPreviewing : isSaving}
              data-test-id={
                applicationId ? 'esign-preview-button' : 'primary-cta-button'
              }
            >
              {applicationId
                ? ESIGN_ACTIVITY_COPY.PREVIEW
                : ESIGN_ACTIVITY_COPY.SAVE}
            </Button>
          )}
        </Flex>
      </Flex>

      <EsignDocumentPreview
        isOpen={Boolean(previewKey)}
        onClose={() => setPreviewKey(undefined)}
        documents={documents}
        initialKey={previewKey}
      />
    </>
  );
};
